import React, {Component} from "react"
import Papa from "papaparse"
import PropTypes from "prop-types"

import {InjectIntl, injectIntl} from "@irati/utils/lang"
import authentication from "@irati/auth"
import Button from "@irati/ui/src/Button"
import IconButton from "@irati/ui/src/Button/IconButton"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import History from "@irati/utils/history"
import adminTracking from "@irati-tracking/admin"
import assignManagersByCSV from "@irati/identity/utils/user/assignManagersByCSV"
import {DONT_INCLUDE, getUserCSVHeaders, getUserCSVRows, csvRowToUser, TEAMS, EMAIL} from "@irati/admin/helpers/userCSV"
import {User} from "@irati/types"

import DeactivatePeopleCSV from "../../../components/Users/AssignManagers/AssignManagersCSV"
import SuccessWithBackLink from "../../../components/SuccessWithBackLink"
import {VIEW_TEAM} from "../../../routes/routeConfig"
import AddPeopleHeaders from "../../../components/Users/AddUsers/AddPeopleHeaders/AddPeopleHeaders"

export enum AssignManagersSteps {
  ASSIGN_MANAGERS_CSV = "assign_managers-csv",
  ASSIGN_MANAGERS_HEADERS = "assign-managers-headers",
  ASSIGN_MANAGERS_SUCCESS = "assign_managers-success"
}

export const OPTIONS = [
  {value: "email", label: "admin.components.column.options.emailAddress"},
  {value: "teams", label: "admin.components.column.options.teams"}
]

interface Props extends InjectIntl {
  goBack: () => void,
  alignLeft: boolean
}

interface FailedUser {
  email: string,
  message: string
}

interface State {
  currentStep: AssignManagersSteps,
  csv: string[],
  headers: string[],
  isLoading: false,
  load: Promise<any> | null,
  failedUsers: FailedUser[],
  assignedManagers: number
}

class DeactivateMultiplePeople extends Component<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }
  state: State = {
    currentStep: AssignManagersSteps.ASSIGN_MANAGERS_CSV,
    csv: [],
    headers: [],
    isLoading: false,
    load: null,
    failedUsers: [],
    assignedManagers: 0
  }

  onDrop = (acceptedFiles: any) => {
    const {formatMessage} = this.props
    let parsedRows = []
    Papa.parse(acceptedFiles[0], {
      header: false,
      skipEmptyLines: true,
      step: (row) => {
        if (row.data[0].find((value) => value.trim() !== "")) {
          parsedRows = [...parsedRows, row.data[0]]
        }
      },
      complete: () => {
        this.setState({
          headers: getUserCSVHeaders(parsedRows[0], formatMessage),
          csv: getUserCSVRows(parsedRows),
          currentStep: AssignManagersSteps.ASSIGN_MANAGERS_HEADERS
        })
      },
      error: (res) => {
        console.log(res)
      }
    })
  }

  updateHeader = (value: string, idx: number) => {
    const headers = this.state.headers
    headers.map((header, i) => {
      if (value && value !== DONT_INCLUDE) {
        if (header === value) {
          headers.splice(i, 1, "")
        }
      }
    })
    headers.splice(idx, 1, value)
    this.setState({headers})
  }

  goBackToCSVStep = () => {
    this.setState({currentStep: AssignManagersSteps.ASSIGN_MANAGERS_CSV})
  }

  createCSVFile = () => {
    const {headers, csv} = this.state
    const data: User[] = []

    csv.forEach((row) => {
      const {user}= csvRowToUser(headers, row)
      data.push({email: user.email, teams: user.teams})
    })
    return Papa.unparse({fields: [EMAIL, TEAMS], data})
  }

  submitUsers = () => {
    const {formatMessage} = this.props
    this.setState({
      failedUsers: [],
      assignedManagers: 0
    })
    const csvFile = this.createCSVFile()
    const load = assignManagersByCSV(
      csvFile,
      formatMessage,
      authentication.business().uuid)
      .then(({failedUsers, assignedManagers}) => {
        adminTracking.bulkAssignManagersSuccessful()
        this.setState(
          {
            failedUsers,
            assignedManagers,
            currentStep: AssignManagersSteps.ASSIGN_MANAGERS_SUCCESS
          }
        )
      })
    this.setState({load})
  }

  handleGoBackClick = () => {
    History.push(VIEW_TEAM, this.context.apps.ADMIN)
  }

  render() {
    const {currentStep, load, assignedManagers, failedUsers, headers, csv, isLoading} = this.state
    const {goBack, formatMessage, alignLeft} = this.props

    return (
      <>
        {goBack ? <IconButton onClick={goBack} icon="backArrow" /> :  <Button tertiary onClick={this.handleGoBackClick} marginBottom={2}>
          <Icon icon={IconTypes.chevronLeft} marginRight />
          {formatMessage({id: "admin.teams.backToTeams.label"})}
        </Button>}
        {currentStep === AssignManagersSteps.ASSIGN_MANAGERS_CSV &&
          <DeactivatePeopleCSV
            alignLeft={alignLeft}
            back={goBack}
            load={load}
            onDrop={this.onDrop} />}
        {currentStep === AssignManagersSteps.ASSIGN_MANAGERS_HEADERS && (
          <AddPeopleHeaders
            alignLeft={alignLeft}
            headers={headers}
            usersLength={csv.length}
            csv={csv.slice(0, 5)}
            updateHeader={this.updateHeader}
            isLoading={isLoading}
            submitUsers={this.submitUsers}
            back={this.goBackToCSVStep}
            requiredHeaders={[EMAIL, TEAMS]}
            buttonText="admin.dashboard.integrations.feedback.button.confirm"
            requiredMessage="admin.components.addPeopleHeaders.headerFour.emailAndTeams"
            options={OPTIONS} />
        )}
        {currentStep === AssignManagersSteps.ASSIGN_MANAGERS_SUCCESS && (
          <SuccessWithBackLink
            message={formatMessage({id: "admin.components.assignManagersCSV.success"}, {uploadedCount: assignedManagers})}
            backLink={VIEW_TEAM}
            failedUsers={failedUsers}
            failedMessage="admin.components.assignManagersCSV.failed" />
        )}
      </>
    )
  }
}

export default injectIntl(DeactivateMultiplePeople)
